import { useMutation } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { FormField } from '@/components/shared/form-field';
import { LoadingButton } from '@/components/shared/loading-button';
import { PasswordInput } from '@/components/shared/password-input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api, setAccessToken } from '@/lib/api';
import { useToast } from '@/lib/toast';

/** Mirrors MIN_PASSWORD_LENGTH in services/api/src/services/auth.ts. */
const MIN_LENGTH = 12;
const EMPTY = { current: '', next: '', confirm: '' };

export function ChangePasswordDialog({ open, onOpenChange }: { open: boolean; onOpenChange(open: boolean): void }) {
  const toast = useToast();
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState(false);
  const change = useMutation({
    mutationFn: () => api<{ access_token: string }>('/admin/v1/auth/password', {
      method: 'POST', body: { current_password: form.current, new_password: form.next },
    }),
    onSuccess: (res) => {
      // The API revokes every session and hands this tab a new one.
      setAccessToken(res.access_token);
      toast('Password changed. Other sessions have been signed out.');
      close();
    },
  });
  const close = () => { setForm(EMPTY); setTouched(false); change.reset(); onOpenChange(false); };

  const errors = {
    next: form.next.length < MIN_LENGTH ? `Must be at least ${MIN_LENGTH} characters` : form.next === form.current ? 'Must differ from the current password' : undefined,
    confirm: form.confirm !== form.next ? 'Passwords do not match' : undefined,
  };
  const valid = form.current.length > 0 && !errors.next && !errors.confirm;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (valid) change.mutate();
  };
  const field = (key: keyof typeof EMPTY) => ({
    id: `pw-${key}`, value: form[key],
    onChange: (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>You'll stay signed in here. Every other session will be signed out.</DialogDescription>
          </DialogHeader>
          <FormField label="Current password" htmlFor="pw-current">
            <PasswordInput {...field('current')} autoComplete="current-password" autoFocus placeholder="Enter your current password" />
          </FormField>
          <FormField label="New password" htmlFor="pw-next" hint={`At least ${MIN_LENGTH} characters`} error={touched || form.next ? errors.next : undefined}>
            <PasswordInput {...field('next')} autoComplete="new-password" placeholder="Enter a new password" />
          </FormField>
          <FormField label="Confirm new password" htmlFor="pw-confirm" error={touched || form.confirm ? errors.confirm : undefined}>
            <PasswordInput {...field('confirm')} autoComplete="new-password" placeholder="Re-enter the new password" />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>Cancel</Button>
            <LoadingButton type="submit" loading={change.isPending} disabled={touched && !valid}>
              <KeyRound /> Change password
            </LoadingButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
